/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import styles from '../../../style';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  CheckOutlined,
  ClearOutlined,
  Edit,
  DeleteOutline,
  CalendarToday,
} from '@material-ui/icons';
import { connect } from 'react-redux';
import {
  saveVaccinations,
  updateVaccinations,
  deleteVaccinations,
  getPatientMedicalHistory,
} from 'services/soap/action';

// ---------------------------------------------------------------------------
// TipButton: IconButton wrapped in a Tooltip.
// The <span> wrapper ensures the tooltip renders even when the button is disabled.
// ---------------------------------------------------------------------------
const TipButton = ({ title, children, ...rest }) => (
  <Tooltip title={title} arrow>
    <span style={{ display: 'inline-flex' }}>
      <IconButton {...rest}>{children}</IconButton>
    </span>
  </Tooltip>
);

const Vaccinations = props => {
  const { classes, patientId, getPatientMedicalHistory, ptMedicalHistory } = props;

  const initialUserState = {
    id: 0,
    name: '',
    dategiven: new Date(),
    lot: '',
  };

  const [values, setValues] = useState(initialUserState);
  const [editvalues, setEditValues] = useState(initialUserState);
  const [vaccination, setVaccination] = useState([]);
  const [isEdit, setIsEdit] = useState(false);

  // ---------------------------------------------------------------------------
  // Loading states
  // 'save'        — header row save button
  // 'update'      — inline update confirm button
  // `delete-{id}` — delete spinner on that specific row
  // ---------------------------------------------------------------------------
  const [loadingId, setLoadingId] = useState(null);

  // ---------------------------------------------------------------------------
  // Sync Redux → local state
  // Guard removed so an empty array properly clears local state too
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (ptMedicalHistory?.vaccinations !== undefined)
      setVaccination(ptMedicalHistory.vaccinations || []);
  }, [ptMedicalHistory]);

  // ---------------------------------------------------------------------------
  // Field handlers
  // ---------------------------------------------------------------------------
  const handleFieldChange = (field, value) =>
    setValues({ ...values, [field]: value });

  const handleEditFieldChange = (field, value) =>
    setEditValues({ ...editvalues, [field]: value });

  // ---------------------------------------------------------------------------
  // Save new vaccination
  // ---------------------------------------------------------------------------
  const handleSaveVaccinations = async () => {
    if (values.name || values.lot) {
      setLoadingId('save');
      try {
        const res = await props.saveVaccinations(patientId, values);
        if (res.status) {
          getPatientMedicalHistory(patientId);
          setValues(initialUserState);
        }
      } finally {
        setLoadingId(null);
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Update existing vaccination
  // ---------------------------------------------------------------------------
  const handleUpdateVaccinations = async idx => {
    if (editvalues.name || editvalues.lot) {
      setLoadingId('update');
      try {
        const res = await props.updateVaccinations(patientId, editvalues);
        if (res.status) {
          getPatientMedicalHistory(patientId);
          setIsEdit(false);
        }
      } finally {
        setLoadingId(null);
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Delete vaccination
  // ---------------------------------------------------------------------------
  const handleDeleteVaccinations = async id => {
    if (loadingId !== null) return;
    setLoadingId(`delete-${id}`);
    try {
      const res = await props.deleteVaccinations(patientId, id);
      if (res.status) {
        setValues(initialUserState);
        getPatientMedicalHistory(patientId);
      }
    } finally {
      setLoadingId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Edit / clear
  // ---------------------------------------------------------------------------
  const handleEditVaccinations = (data, index) => {
    setIsEdit(true);
    if (data)
      setEditValues({
        id: data.id,
        name: data.name,
        dategiven: data.dategiven,
        lot: data.lot,
        editIndex: index,
      });
  };

  const clearVaccinations = () => {
    setIsEdit(false);
    setValues(initialUserState);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className={classes.templateWrap}>
      <div className={classes.templateBody} style={{ borderTop: 'default' }}>
        <PerfectScrollbar>
          <div style={{ maxHeight: 'calc(100vh - 298px)' }}>
            <Table className={classes.templateTable}>

              {/* ==============================================================
                HEADER ROW — add new vaccination
            ============================================================== */}
              <TableHead>
                <TableRow>
                  <TableCell width="20%">
                    <TextField
                      className={classes.textBox}
                      InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                      onChange={e => handleFieldChange('name', e.target.value)}
                      placeholder="Input Name"
                      value={values.name}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell width="30%">
                    <DatePicker
                      customInput={
                        <TextField
                          className={classes.textField}
                          fullWidth
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <CalendarToday style={{ height: 16, width: 16 }} />
                              </InputAdornment>
                            ),
                          }}
                          margin="dense"
                          variant="outlined"
                        />
                      }
                      dateFormat="MM/dd/yyyy"
                      onBlur={e => (e.target.placeholder = 'Date Given')}
                      onChange={date => handleFieldChange('dategiven', date)}
                      onFocus={e => (e.target.placeholder = '')}
                      placeholderText="Date Given"
                      selected={values.dategiven && new Date(values.dategiven)}
                      showMonthDropdown
                      showYearDropdown
                    />
                  </TableCell>
                  <TableCell width="60%">
                    <TextField
                      className={classes.textBox}
                      InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                      onChange={e => handleFieldChange('lot', e.target.value)}
                      placeholder="Comments"
                      value={values.lot}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                    <TipButton
                      title="Save"
                      disabled={loadingId === 'save'}
                      onClick={handleSaveVaccinations}
                      size="small"
                      style={{ color: '#03A484' }}>
                      {loadingId === 'save'
                        ? <CircularProgress size={16} color="inherit" />
                        : <CheckOutlined />}
                    </TipButton>
                    <TipButton
                      title="Clear"
                      disabled={loadingId === 'save'}
                      onClick={clearVaccinations}
                      size="small">
                      <ClearOutlined style={{ color: '#DE5555' }} />
                    </TipButton>
                  </TableCell>
                </TableRow>
              </TableHead>

              {/* ==============================================================
                BODY — existing vaccination rows
            ============================================================== */}
              <TableBody>
                {vaccination &&
                  vaccination.length > 0 &&
                  vaccination.map((item, idx) => (
                    <TableRow key={item.id}>
                      {isEdit && idx === editvalues.editIndex ? (
                        <>
                          <TableCell width="20%">
                            <TextField
                              className={classes.textBox}
                              InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                              onChange={e => handleEditFieldChange('name', e.target.value)}
                              placeholder="Input Name"
                              value={editvalues.name}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell width="30%">
                            <DatePicker
                              customInput={
                                <TextField
                                  className={classes.textField}
                                  fullWidth
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <CalendarToday style={{ height: 16, width: 16 }} />
                                      </InputAdornment>
                                    ),
                                  }}
                                  margin="dense"
                                  variant="outlined"
                                />
                              }
                              dateFormat="MM/dd/yyyy"
                              onBlur={e => (e.target.placeholder = 'Date Given')}
                              onChange={date => handleEditFieldChange('dategiven', date)}
                              onFocus={e => (e.target.placeholder = '')}
                              placeholderText="Date Given"
                              selected={editvalues.dategiven && new Date(editvalues.dategiven)}
                              showMonthDropdown
                              showYearDropdown
                            />
                          </TableCell>
                          <TableCell width="60%">
                            <TextField
                              className={classes.textBox}
                              InputProps={{ classes: { notchedOutline: classes.notchedOutline } }}
                              onChange={e => handleEditFieldChange('lot', e.target.value)}
                              placeholder="Comments"
                              value={editvalues.lot}
                              variant="outlined"
                            />
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{item?.name}</TableCell>
                          <TableCell>
                            {item?.dategiven
                              ? moment(item.dategiven).format('MM/DD/YYYY')
                              : ''}
                          </TableCell>
                          <TableCell>{item?.lot}</TableCell>
                        </>
                      )}
                      <TableCell align="center" width={72}>
                        {isEdit && idx === editvalues.editIndex ? (
                          <>
                            <TipButton
                              title="Update Vaccination"
                              disabled={loadingId === 'update'}
                              onClick={() => handleUpdateVaccinations(idx)}
                              size="small"
                              style={{ color: '#03A484' }}>
                              {loadingId === 'update'
                                ? <CircularProgress size={16} color="inherit" />
                                : <CheckOutlined />}
                            </TipButton>
                            <TipButton
                              title="Cancel"
                              disabled={loadingId === 'update'}
                              onClick={clearVaccinations}
                              size="small">
                              <ClearOutlined style={{ color: '#DE5555' }} />
                            </TipButton>
                          </>
                        ) : (
                          <>
                            <TipButton
                              title="Edit Vaccination"
                              disabled={loadingId !== null}
                              onClick={() => handleEditVaccinations(item, idx)}
                              size="small">
                              <Edit color="primary" />
                            </TipButton>
                            <TipButton
                              title="Delete Vaccination"
                              disabled={loadingId !== null}
                              onClick={() => handleDeleteVaccinations(item.id)}
                              size="small">
                              {loadingId === `delete-${item.id}`
                                ? <CircularProgress size={16} color="inherit" />
                                : <DeleteOutline style={{ color: '#DE5555' }} />}
                            </TipButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </PerfectScrollbar>
      </div>
    </div>
  );
};

Vaccinations.propTypes = {
  className: PropTypes.string,
};

const mapDispatchToProps = {
  saveVaccinations,
  updateVaccinations,
  deleteVaccinations,
  getPatientMedicalHistory,
};

const mapStateToProps = state => ({
  ptMedicalHistory: state.soap && state.soap.ptMedicalHistory,
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Vaccinations))
);